import { Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { COUNTRIES, RECOMMENDED, roundFlagUrl, type CountrySlug } from "@/data/countries";

interface Props {
  activeCountry?: CountrySlug;
}

export function ArticleSidebar({ activeCountry }: Props) {
  const featured = activeCountry
    ? RECOMMENDED[activeCountry].map((a) => ({ ...a, countrySlug: activeCountry }))
    : COUNTRIES.flatMap((c) =>
        RECOMMENDED[c.slug].slice(0, 1).map((a) => ({ ...a, countrySlug: c.slug })),
      );

  return (
    <aside className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
          Recommended reads
        </h3>
        <ul className="mt-4 space-y-3">
          {featured.map((a) => {
            const c = COUNTRIES.find((x) => x.slug === a.countrySlug)!;
            return (
              <li key={`${c.slug}-${a.slug}`}>
                <Link title={`Read ${a.title} in the ${c.name} guide`}
                  to={"/$country"}
                  params={{ country: c.slug }}
                  className="group flex items-start gap-3"
                >
                  <SafeImage
                    src={roundFlagUrl(c.code)}
                    alt={`${c.name} flag`}
                    width={28}
                    height={28}
                    loading="lazy" decoding="async"
                    className="mt-0.5 h-7 w-7 shrink-0 rounded-full ring-1 ring-border"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground leading-snug group-hover:text-foreground">
                      {a.title}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {c.shortName} · {a.category}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="rounded-xl border border-border bg-secondary/60 p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
          Browse by country
        </h3>
        <ul className="mt-4 space-y-2">
          {COUNTRIES.map((c) => (
            <li key={c.slug}>
              <Link title={`Browse the ${c.name} immigration, jobs and tax guide`}
                to={"/$country"}
                params={{ country: c.slug }}
                className="flex items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground/80 hover:bg-card hover:text-foreground"
              >
                <SafeImage
                  src={roundFlagUrl(c.code)}
                  alt={`${c.name} flag — ${c.name} expat guide`}
                  width={22}
                  height={22}
                  loading="lazy" decoding="async"
                  className="h-[22px] w-[22px] rounded-full ring-1 ring-border"
                />
                <span className="font-medium">{c.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

    </aside>
  );
}
